import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Store, CheckCircle, XCircle, Clock, Eye, X, Mail, Phone, Building2, Loader2,
    MapPin, FileText, ShieldCheck, DollarSign, Package, ShoppingCart, TrendingUp,
    Calendar, Globe, CreditCard, Award, ExternalLink, BarChart3, Boxes,
    ChevronDown, ChevronRight, RefreshCw, AlertCircle, Ban, Star, Hash, IndianRupee, Trash2, PauseCircle, Percent
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import DataTable from '../components/dashboard/DataTable';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../App';

const AdminVendors = () => {
    const { token } = useAuth();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [vendorDetails, setVendorDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [activeTab, setActiveTab] = useState('overview');
    const [editingCommissionRate, setEditingCommissionRate] = useState('');
    const [isSavingCommission, setIsSavingCommission] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        contact: true, business: true, bank: false, documents: true, inventory: true, orders: true, financials: true
    });

    useEffect(() => {
        if (token) fetchVendors();
    }, [token]);

    const fetchVendors = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/api/vendors`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVendors(response.data);
        } catch (error) {
            console.error('Error fetching vendors:', error);
            toast.error('Failed to fetch vendors');
        } finally {
            setLoading(false);
        }
    };

    const fetchVendorDetails = useCallback(async (vendorId) => {
        setDetailsLoading(true);
        setVendorDetails(null);
        try {
            const response = await axios.get(`${API}/api/admin/vendors/${vendorId}/full-details`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVendorDetails(response.data);
            setEditingCommissionRate(response.data.vendor.commission_rate ?? '');
        } catch (error) {
            console.error('Error fetching vendor details:', error);
            toast.error('Failed to load vendor details');
        }
        setDetailsLoading(false);
    }, [token]);

    const openVendorDetails = (vendor) => {
        setSelectedVendor(vendor);
        setActiveTab('overview');
        fetchVendorDetails(vendor.id);
    };

    const updateVendorStatus = async (vendorId, newStatus) => {
        try {
            setActionLoading(vendorId);
            const endpoint = newStatus === 'approved' ? 'approve' : 'reject';
            await axios.put(
                `${API}/api/admin/vendors/${vendorId}/${endpoint}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setVendors(vendors.map(v => v.id === vendorId ? { ...v, status: newStatus } : v));
            if (selectedVendor?.id === vendorId) {
                setSelectedVendor({ ...selectedVendor, status: newStatus });
                if (vendorDetails) {
                    setVendorDetails({ ...vendorDetails, vendor: { ...vendorDetails.vendor, status: newStatus } });
                }
            }
            toast.success(newStatus === 'approved' ? 'Vendor approved!' : 'Vendor rejected');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update vendor status');
        } finally {
            setActionLoading(null);
        }
    };

    const deleteVendor = async (vendorId) => {
        if (!window.confirm("Are you ABSOLUTELY sure you want to completely erase this vendor application? This will demote the user back to customer and cannot be undone.")) return;
        try {
            setActionLoading(vendorId);
            await axios.delete(
                `${API}/api/admin/vendors/${vendorId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Vendor application successfully deleted");
            setVendors(prev => prev.filter(v => v.id !== vendorId));
            setSelectedVendor(null);
            setVendorDetails(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to delete vendor");
        } finally {
            setActionLoading(null);
        }
    };

    const updateVendorCommission = async (vendorId) => {
        try {
            setIsSavingCommission(true);
            const rateStr = String(editingCommissionRate).trim();
            const rate = rateStr === '' ? null : parseFloat(rateStr);
            if (rate !== null && (isNaN(rate) || rate < 0 || rate > 100)) {
                toast.error("Invalid commission rate. Must be between 0 and 100.");
                return;
            }
            await axios.put(`${API}/api/admin/vendors/${vendorId}/commission`, { commission_rate: rate }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Vendor commission rate updated successfully");
            setVendorDetails(prev => ({
                ...prev,
                vendor: { ...prev.vendor, commission_rate: rate }
            }));
            setVendors(prev => prev.map(v => v.id === vendorId ? { ...v, commission_rate: rate } : v));
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to update commission rate");
        } finally {
            setIsSavingCommission(false);
        }
    };

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const filteredVendors = statusFilter === 'all' ? vendors : vendors.filter(v => v.status === statusFilter);

    const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

    const statusConfig = {
        approved: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, dot: 'bg-emerald-500' },
        pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, dot: 'bg-amber-500' },
        rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, dot: 'bg-red-500' },
        suspended: { color: 'bg-orange-100 text-orange-800', icon: PauseCircle, dot: 'bg-orange-500' }
    };

    const orderStatusColors = {
        pending: 'bg-amber-100 text-amber-700',
        confirmed: 'bg-blue-100 text-blue-700',
        processing: 'bg-indigo-100 text-indigo-700',
        shipped: 'bg-purple-100 text-purple-700',
        delivered: 'bg-emerald-100 text-emerald-700',
        completed: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700',
    };

    const getStatusBadge = (status) => {
        const c = statusConfig[status] || statusConfig.pending;
        return (
            <Badge className={`${c.color} gap-1`}>
                <c.icon className="w-3 h-3" />
                {status}
            </Badge>
        );
    };

    // Document labels for nice display
    const documentLabels = {
        pan_card_url: { label: 'PAN Card', icon: CreditCard },
        gst_certificate_url: { label: 'GST Certificate', icon: FileText },
        bis_certification_url: { label: 'BIS Certification', icon: Award },
        mnre_registration_url: { label: 'MNRE Registration', icon: ShieldCheck },
        nabl_test_reports_url: { label: 'NABL Test Reports', icon: FileText },
        manufacturer_authorization_url: { label: 'Manufacturer Authorization', icon: FileText },
        bill_of_entry_url: { label: 'Bill of Entry', icon: FileText },
        iec_code_url: { label: 'IEC Code', icon: Globe },
        company_registration_url: { label: 'Company Registration', icon: Building2 },
    };

    // ─── TABLE COLUMNS ────────────────────────
    const columns = [
        {
            key: 'name',
            label: 'Vendor',
            render: (value, row) => (
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                            {(row.business_name || value || '?')[0].toUpperCase()}
                        </span>
                    </div>
                    <div className="min-w-0">
                        <p className="font-medium truncate">{row.business_name || value}</p>
                        <p className="text-xs text-muted-foreground truncate">{value}</p>
                    </div>
                </div>
            )
        },
        {
            key: 'business_type',
            label: 'Type',
            render: (value) => (
                <span className="text-sm capitalize">{value || '—'}</span>
            )
        },
        { key: 'status', label: 'Status', render: (value) => getStatusBadge(value) },
        {
            key: 'city',
            label: 'Location',
            render: (value, row) => (
                <span className="text-sm text-muted-foreground">
                    {[value, row.state].filter(Boolean).join(', ') || '—'}
                </span>
            )
        },
        {
            key: 'created_at',
            label: 'Applied',
            render: (value) => <span className="text-sm text-muted-foreground">{formatDate(value)}</span>
        },
        {
            key: 'actions',
            label: 'Quick Actions',
            sortable: false,
            render: (_, row) => (
                row.status === 'pending' ? (
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs px-2"
                            onClick={(e) => { e.stopPropagation(); updateVendorStatus(row.id, 'approved'); }}
                            disabled={actionLoading === row.id}
                        >
                            {actionLoading === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Approve
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 h-7 text-xs px-2"
                            onClick={(e) => { e.stopPropagation(); updateVendorStatus(row.id, 'rejected'); }}
                            disabled={actionLoading === row.id}
                        >
                            <XCircle className="w-3 h-3 mr-1" />
                            Reject
                        </Button>
                    </div>
                ) : (
                    <Badge variant="outline" className="text-xs">{row.status}</Badge>
                )
            )
        }
    ];

    const tableActions = [
        { label: 'View Full Profile', icon: Eye, onClick: (row) => openVendorDetails(row) }
    ];

    const filterCards = [
        { key: 'all', label: 'All Vendors', icon: Store, color: 'text-purple-600', bg: 'bg-purple-100', gradient: 'from-purple-500/10 to-purple-600/5' },
        { key: 'pending', label: 'Pending', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100', gradient: 'from-amber-500/10 to-amber-600/5' },
        { key: 'approved', label: 'Approved', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-100', gradient: 'from-emerald-500/10 to-emerald-600/5' },
        { key: 'suspended', label: 'Suspended', icon: PauseCircle, color: 'text-orange-600', bg: 'bg-orange-100', gradient: 'from-orange-500/10 to-orange-600/5' },
        { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', gradient: 'from-red-500/10 to-red-600/5' },
    ];

    const roleCounts = {
        all: vendors.length,
        pending: vendors.filter(v => v.status === 'pending').length,
        approved: vendors.filter(v => v.status === 'approved').length,
        rejected: vendors.filter(v => v.status === 'rejected').length,
        suspended: vendors.filter(v => v.status === 'suspended').length,
    };

    // ─── COLLAPSIBLE SECTION COMPONENT ────────────────────────
    const CollapsibleSection = ({ sectionKey, title, icon: Icon, iconColor, badgeText, children }) => (
        <div className="border rounded-xl overflow-hidden">
            <button
                onClick={() => toggleSection(sectionKey)}
                className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
            >
                <div className="flex items-center gap-2.5">
                    <Icon className={`w-4 h-4 ${iconColor}`} />
                    <span className="font-semibold text-sm">{title}</span>
                    {badgeText && <Badge variant="outline" className="text-xs ml-1">{badgeText}</Badge>}
                </div>
                {expandedSections[sectionKey] ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </button>
            <AnimatePresence>
                {expandedSections[sectionKey] && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="p-4">{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    // ─── DETAIL ROW ────────────────────────
    const DetailRow = ({ icon: Icon, label, value, iconColor = 'text-muted-foreground', mono = false }) => (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/20">
            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
            <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className={`font-medium text-sm truncate ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
            </div>
        </div>
    );

    // ─── MODAL TABS ────────────────────────
    const tabs = [
        { key: 'overview', label: 'Overview', icon: Eye },
        { key: 'inventory', label: 'Inventory', icon: Package },
        { key: 'orders', label: 'Orders', icon: ShoppingCart },
        { key: 'documents', label: 'Documents', icon: FileText },
    ];

    const vd = vendorDetails?.vendor || selectedVendor || {};
    const stats = vendorDetails?.stats || {};
    const inventory = vendorDetails?.inventory || [];
    const orders = vendorDetails?.orders || [];
    const docs = vd.documents || {};
    const docCount = Object.values(docs).filter(Boolean).length;

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                                <Store className="w-5 h-5 text-purple-600" />
                            </div>
                            Vendor Management
                        </h1>
                        <p className="text-muted-foreground mt-1 ml-13">Review applications, manage vendor profiles, and monitor performance</p>
                    </div>
                    <Button variant="outline" onClick={fetchVendors} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {/* Filter Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {filterCards.map(({ key, label, icon: Icon, color, bg, gradient }) => {
                        const isActive = statusFilter === key;
                        return (
                            <motion.button
                                key={key}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setStatusFilter(key)}
                                className={`bg-card rounded-xl border p-4 text-left transition-all ${isActive ? 'ring-2 ring-primary shadow-md' : 'hover:border-primary/50 hover:shadow-sm'
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center`}>
                                        <Icon className={`w-4.5 h-4.5 ${color}`} />
                                    </div>
                                    {isActive && <div className="w-2 h-2 rounded-full bg-primary" />}
                                </div>
                                <p className="text-2xl font-bold">{roleCounts[key]}</p>
                                <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
                            </motion.button>
                        );
                    })}
                </div>

                {/* Pending Banner */}
                {roleCounts.pending > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3"
                    >
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-amber-800">
                                {roleCounts.pending} pending application{roleCounts.pending > 1 ? 's' : ''} require{roleCounts.pending === 1 ? 's' : ''} your review
                            </p>
                            <p className="text-xs text-amber-600 mt-0.5">Click on a vendor to review their documents and business details</p>
                        </div>
                    </motion.div>
                )}

                {/* Loading / Table */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-48 gap-3 bg-card rounded-xl border">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Loading vendors...</p>
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredVendors}
                        actions={tableActions}
                        searchPlaceholder="Search by business name, contact, location..."
                        onRowClick={(row) => openVendorDetails(row)}
                    />
                )}

                {/* ═══════════════════ VENDOR DETAIL MODAL ═══════════════════ */}
                <AnimatePresence>
                    {selectedVendor && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                            onClick={() => { setSelectedVendor(null); setVendorDetails(null); }}
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                                className="bg-card rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* ─── Modal Header ─── */}
                                <div className="p-5 border-b flex-shrink-0">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                                                <span className="text-2xl font-bold text-white">
                                                    {(vd.business_name || vd.name || '?')[0].toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold">{vd.business_name || vd.name}</h2>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {getStatusBadge(vd.status || 'pending')}
                                                    {vd.business_type && (
                                                        <Badge variant="outline" className="capitalize text-xs">{vd.business_type}</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="icon" onClick={() => { setSelectedVendor(null); setVendorDetails(null); }}>
                                            <X className="w-5 h-5" />
                                        </Button>
                                    </div>

                                    {/* Tab Navigation */}
                                    <div className="flex gap-1 mt-4 bg-muted/50 rounded-lg p-1">
                                        {tabs.map(({ key, label, icon: Icon }) => (
                                            <button
                                                key={key}
                                                onClick={() => setActiveTab(key)}
                                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all ${activeTab === key
                                                        ? 'bg-card shadow-sm text-primary'
                                                        : 'text-muted-foreground hover:text-foreground'
                                                    }`}
                                            >
                                                <Icon className="w-3.5 h-3.5" />
                                                {label}
                                                {key === 'inventory' && inventory.length > 0 && (
                                                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 rounded-full">{inventory.length}</span>
                                                )}
                                                {key === 'orders' && orders.length > 0 && (
                                                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 rounded-full">{orders.length}</span>
                                                )}
                                                {key === 'documents' && docCount > 0 && (
                                                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 rounded-full">{docCount}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* ─── Modal Body ─── */}
                                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                                    {detailsLoading ? (
                                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                            <p className="text-sm text-muted-foreground">Loading vendor details...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* ━━━ OVERVIEW TAB ━━━ */}
                                            {activeTab === 'overview' && (
                                                <div className="space-y-4">
                                                    {/* Performance Stats */}
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                        {[
                                                            { label: 'Total Revenue', value: formatCurrency(stats.total_revenue), icon: IndianRupee, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                                                            { label: 'Total Orders', value: stats.total_orders || 0, icon: ShoppingCart, color: 'text-blue-600', bg: 'bg-blue-50' },
                                                            { label: 'Completed', value: stats.completed_orders || 0, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
                                                            { label: 'Active Orders', value: stats.active_orders || 0, icon: TrendingUp, color: 'text-orange-600', bg: 'bg-orange-50' },
                                                            { label: 'Products Listed', value: stats.total_products || 0, icon: Package, color: 'text-purple-600', bg: 'bg-purple-50' },
                                                            { label: 'Total Stock', value: stats.total_stock || 0, icon: Boxes, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                                                        ].map(({ label, value, icon: Icon, color, bg }) => (
                                                            <div key={label} className={`${bg} rounded-xl p-3.5 border`}>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <Icon className={`w-4 h-4 ${color}`} />
                                                                    <span className="text-xs text-muted-foreground">{label}</span>
                                                                </div>
                                                                <p className="text-lg font-bold">{value}</p>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Contact Information */}
                                                    <CollapsibleSection sectionKey="contact" title="Contact Information" icon={Phone} iconColor="text-blue-500">
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <DetailRow icon={Building2} label="Contact Person" value={vd.name} iconColor="text-blue-500" />
                                                            <DetailRow icon={Mail} label="Email" value={vd.email} iconColor="text-red-500" />
                                                            <DetailRow icon={Phone} label="Phone" value={vd.phone} iconColor="text-green-500" />
                                                            <DetailRow icon={Calendar} label="Applied On" value={formatDate(vd.created_at)} iconColor="text-orange-500" />
                                                        </div>
                                                    </CollapsibleSection>

                                                    {/* Business Details */}
                                                    <CollapsibleSection sectionKey="business" title="Business Details" icon={Building2} iconColor="text-indigo-500">
                                                        <div className="space-y-3">
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <DetailRow icon={Store} label="Business Name" value={vd.business_name} iconColor="text-purple-500" />
                                                                <DetailRow icon={Star} label="Business Type" value={vd.business_type} iconColor="text-yellow-500" />
                                                                <DetailRow icon={Hash} label="GST Number" value={vd.gst_number} iconColor="text-indigo-500" mono />
                                                                <DetailRow icon={CreditCard} label="PAN Number" value={vd.pan_number} iconColor="text-pink-500" mono />
                                                                <DetailRow icon={MapPin} label="City" value={vd.city} iconColor="text-red-500" />
                                                                <DetailRow icon={MapPin} label="State" value={vd.state} iconColor="text-red-400" />
                                                            </div>
                                                            {vd.business_address && (
                                                                <DetailRow icon={MapPin} label="Full Address" value={`${vd.business_address}${vd.pincode ? ` - ${vd.pincode}` : ''}`} iconColor="text-red-500" />
                                                            )}
                                                            {vd.description && (
                                                                <div className="bg-muted/20 rounded-lg p-3">
                                                                    <p className="text-xs text-muted-foreground mb-1">Business Description</p>
                                                                    <p className="text-sm">{vd.description}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </CollapsibleSection>

                                                    {/* Financial Settings */}
                                                    {vd.status === 'approved' && (
                                                        <CollapsibleSection sectionKey="financials" title="Financial Settings" icon={Percent} iconColor="text-emerald-500">
                                                            <div className="space-y-4">
                                                                <div className="flex flex-col gap-2">
                                                                    <label className="text-sm font-medium text-muted-foreground">Custom Commission Rate (%)</label>
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="relative flex-1 max-w-[200px]">
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                max="100"
                                                                                step="0.01"
                                                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                                placeholder="Default platform rate"
                                                                                value={editingCommissionRate}
                                                                                onChange={(e) => setEditingCommissionRate(e.target.value)}
                                                                            />
                                                                            <span className="absolute right-3 top-2.5 text-muted-foreground">%</span>
                                                                        </div>
                                                                        <Button 
                                                                            size="sm" 
                                                                            className="bg-primary hover:bg-primary/90"
                                                                            disabled={isSavingCommission}
                                                                            onClick={() => updateVendorCommission(vd.id)}
                                                                        >
                                                                            {isSavingCommission ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                                                            Save Rate
                                                                        </Button>
                                                                    </div>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        Leave empty to use the global default platform commission rate.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </CollapsibleSection>
                                                    )}

                                                    {/* Bank Details */}
                                                    {vd.bank_details && (
                                                        <CollapsibleSection sectionKey="bank" title="Bank Details" icon={CreditCard} iconColor="text-green-500">
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <DetailRow icon={Building2} label="Bank Name" value={vd.bank_details.bank_name} iconColor="text-green-600" />
                                                                <DetailRow icon={Building2} label="Account Holder" value={vd.bank_details.account_holder_name} iconColor="text-green-600" />
                                                                <DetailRow icon={Hash} label="Account Number" value={vd.bank_details.account_number ? `****${vd.bank_details.account_number.slice(-4)}` : '—'} iconColor="text-green-600" mono />
                                                                <DetailRow icon={Hash} label="IFSC Code" value={vd.bank_details.ifsc_code} iconColor="text-green-600" mono />
                                                                {vd.bank_details.branch_name && (
                                                                    <DetailRow icon={MapPin} label="Branch" value={vd.bank_details.branch_name} iconColor="text-green-600" />
                                                                )}
                                                            </div>
                                                        </CollapsibleSection>
                                                    )}
                                                </div>
                                            )}

                                            {/* ━━━ INVENTORY TAB ━━━ */}
                                            {activeTab === 'inventory' && (
                                                <div className="space-y-3">
                                                    {inventory.length === 0 ? (
                                                        <div className="text-center py-12 text-muted-foreground">
                                                            <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                                            <p className="font-medium">No inventory items</p>
                                                            <p className="text-sm mt-1">This vendor hasn't listed any products yet</p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {/* Inventory Summary */}
                                                            <div className="grid grid-cols-3 gap-3 mb-2">
                                                                <div className="bg-purple-50 rounded-xl p-3 border text-center">
                                                                    <p className="text-lg font-bold text-purple-700">{inventory.length}</p>
                                                                    <p className="text-xs text-muted-foreground">Products</p>
                                                                </div>
                                                                <div className="bg-blue-50 rounded-xl p-3 border text-center">
                                                                    <p className="text-lg font-bold text-blue-700">{stats.total_stock || 0}</p>
                                                                    <p className="text-xs text-muted-foreground">Total Stock</p>
                                                                </div>
                                                                <div className="bg-emerald-50 rounded-xl p-3 border text-center">
                                                                    <p className="text-lg font-bold text-emerald-700">
                                                                        {inventory.filter(i => i.is_available !== false).length}
                                                                    </p>
                                                                    <p className="text-xs text-muted-foreground">Available</p>
                                                                </div>
                                                            </div>

                                                            {/* Inventory List */}
                                                            <div className="border rounded-xl overflow-hidden">
                                                                <table className="w-full">
                                                                    <thead className="bg-muted/50">
                                                                        <tr>
                                                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Product</th>
                                                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Price</th>
                                                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Stock</th>
                                                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Status</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {inventory.map((item, i) => (
                                                                            <tr key={item.id || i} className="border-t hover:bg-muted/20 transition-colors">
                                                                                <td className="px-4 py-3">
                                                                                    <p className="font-medium text-sm truncate max-w-[200px]">{item.product_name || item.product_id}</p>
                                                                                </td>
                                                                                <td className="px-4 py-3 text-sm font-medium">{formatCurrency(item.vendor_price || item.sell_price)}</td>
                                                                                <td className="px-4 py-3">
                                                                                    <span className={`text-sm font-medium ${item.quantity <= 0 ? 'text-red-600' : item.quantity <= 5 ? 'text-amber-600' : 'text-green-600'}`}>
                                                                                        {item.quantity}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-4 py-3">
                                                                                    <Badge className={item.is_available !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                                                                                        {item.is_available !== false ? 'Available' : 'Unavailable'}
                                                                                    </Badge>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {/* ━━━ ORDERS TAB ━━━ */}
                                            {activeTab === 'orders' && (
                                                <div className="space-y-3">
                                                    {orders.length === 0 ? (
                                                        <div className="text-center py-12 text-muted-foreground">
                                                            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                                            <p className="font-medium">No orders yet</p>
                                                            <p className="text-sm mt-1">This vendor hasn't received any orders</p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {/* Order Summary */}
                                                            <div className="grid grid-cols-4 gap-3 mb-2">
                                                                <div className="bg-blue-50 rounded-xl p-3 border text-center">
                                                                    <p className="text-lg font-bold text-blue-700">{stats.total_orders}</p>
                                                                    <p className="text-xs text-muted-foreground">Total</p>
                                                                </div>
                                                                <div className="bg-amber-50 rounded-xl p-3 border text-center">
                                                                    <p className="text-lg font-bold text-amber-700">{stats.active_orders}</p>
                                                                    <p className="text-xs text-muted-foreground">Active</p>
                                                                </div>
                                                                <div className="bg-emerald-50 rounded-xl p-3 border text-center">
                                                                    <p className="text-lg font-bold text-emerald-700">{stats.completed_orders}</p>
                                                                    <p className="text-xs text-muted-foreground">Completed</p>
                                                                </div>
                                                                <div className="bg-green-50 rounded-xl p-3 border text-center">
                                                                    <p className="text-lg font-bold text-green-700">{formatCurrency(stats.total_revenue)}</p>
                                                                    <p className="text-xs text-muted-foreground">Revenue</p>
                                                                </div>
                                                            </div>

                                                            {/* Orders List */}
                                                            <div className="space-y-2">
                                                                {orders.map((order, i) => (
                                                                    <div key={order.id || i} className="border rounded-xl p-4 hover:bg-muted/10 transition-colors">
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="font-mono text-xs text-muted-foreground">
                                                                                    #{(order.id || '').slice(-8).toUpperCase()}
                                                                                </span>
                                                                                <Badge className={orderStatusColors[order.status] || 'bg-gray-100 text-gray-700'}>
                                                                                    {order.status}
                                                                                </Badge>
                                                                            </div>
                                                                            <span className="font-bold text-sm">{formatCurrency(order.total_amount)}</span>
                                                                        </div>
                                                                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                                            <span>
                                                                                Customer: <span className="font-medium text-foreground">{order.customer?.name || order.customer_name || '—'}</span>
                                                                            </span>
                                                                            <span>{formatDateTime(order.created_at)}</span>
                                                                        </div>
                                                                        {order.items && order.items.length > 0 && (
                                                                            <div className="mt-2 flex flex-wrap gap-1">
                                                                                {order.items.slice(0, 3).map((item, idx) => (
                                                                                    <Badge key={idx} variant="outline" className="text-xs">
                                                                                        {item.name || item.product_name || 'Product'} × {item.quantity || 1}
                                                                                    </Badge>
                                                                                ))}
                                                                                {order.items.length > 3 && (
                                                                                    <Badge variant="outline" className="text-xs">+{order.items.length - 3} more</Badge>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {/* ━━━ DOCUMENTS TAB ━━━ */}
                                            {activeTab === 'documents' && (
                                                <div className="space-y-3">
                                                    {docCount === 0 ? (
                                                        <div className="text-center py-12 text-muted-foreground">
                                                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                                            <p className="font-medium">No documents uploaded</p>
                                                            <p className="text-sm mt-1">This vendor hasn't submitted any documents yet</p>
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            {Object.entries(documentLabels).map(([key, { label, icon: DocIcon }]) => {
                                                                const url = docs[key];
                                                                if (!url) return null;
                                                                return (
                                                                    <a
                                                                        key={key}
                                                                        href={`${API}${url}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="flex items-center gap-3 p-4 bg-card border rounded-xl hover:border-primary hover:shadow-sm transition-all group"
                                                                    >
                                                                        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                                                                            <DocIcon className="w-5 h-5 text-amber-600" />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-medium text-sm">{label}</p>
                                                                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                                                <CheckCircle className="w-3 h-3 text-emerald-500" /> Uploaded
                                                                            </p>
                                                                        </div>
                                                                        <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                                                    </a>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* Documents not uploaded */}
                                                    {Object.entries(documentLabels).some(([key]) => !docs[key]) && (
                                                        <div className="mt-4">
                                                            <p className="text-xs text-muted-foreground mb-2 font-semibold">Not Uploaded</p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {Object.entries(documentLabels).map(([key, { label }]) => {
                                                                    if (docs[key]) return null;
                                                                    return (
                                                                        <Badge key={key} variant="outline" className="text-xs text-muted-foreground bg-muted/30">
                                                                            <XCircle className="w-3 h-3 mr-1 text-red-400" />
                                                                            {label}
                                                                        </Badge>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* ─── Modal Footer ─── */}
                                <div className="p-4 border-t flex-shrink-0 flex items-center gap-3">
                                    {(vd.status === 'pending' || vd.status === 'rejected') && (
                                        <Button
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                            onClick={() => updateVendorStatus(selectedVendor.id, 'approved')}
                                            disabled={actionLoading === selectedVendor?.id}
                                        >
                                            {actionLoading === selectedVendor?.id
                                                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                : <CheckCircle className="w-4 h-4 mr-2" />
                                            }
                                            Approve Vendor
                                        </Button>
                                    )}
                                    {vd.status === 'pending' && (
                                        <Button
                                            variant="outline"
                                            className="text-red-600 hover:bg-red-50"
                                            onClick={() => updateVendorStatus(selectedVendor.id, 'rejected')}
                                            disabled={actionLoading === selectedVendor?.id}
                                        >
                                            <XCircle className="w-4 h-4 mr-2" />
                                            Reject
                                        </Button>
                                    )}
                                    {vd.status === 'approved' && (
                                        <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 rounded-lg px-3 py-2 text-sm">
                                            <CheckCircle className="w-4 h-4" />
                                            Vendor approved
                                        </div>
                                    )}
                                    
                                    {vd.status === 'approved' && (
                                        <Button
                                            variant="outline"
                                            className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                            onClick={() => updateVendorStatus(selectedVendor.id, 'suspended')}
                                            disabled={actionLoading === selectedVendor?.id}
                                        >
                                            <PauseCircle className="w-4 h-4 mr-2" /> Suspend Vendor
                                        </Button>
                                    )}

                                    {vd.status === 'suspended' && (
                                        <Button
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                            onClick={() => updateVendorStatus(selectedVendor.id, 'approved')}
                                            disabled={actionLoading === selectedVendor?.id}
                                        >
                                            <CheckCircle className="w-4 h-4 mr-2" /> Restore Vendor
                                        </Button>
                                    )}

                                    <Button
                                        variant="outline"
                                        className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                                        onClick={() => deleteVendor(selectedVendor.id)}
                                        disabled={actionLoading === selectedVendor?.id}
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete App
                                    </Button>

                                    <div className="flex-1" />
                                    <Button variant="outline" onClick={() => { setSelectedVendor(null); setVendorDetails(null); }}>
                                        Close
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </DashboardLayout>
    );
};

export default AdminVendors;
